import { ChapterVerse, Timestamp } from '../types/timestamp';
import { Timestamps } from '../types/timestamps';

export const filterTimestamps = (bbbId: string | undefined, ts: Timestamps[] | undefined) => {
  return ts?.find(timestamp => timestamp.id === bbbId)?.seg ?? [];
};

/**
 * Detects a chapter intro by scanning the segment list for a verse-0 marker.
 *
 * The intro lives inside `seg` as `{ c: chapter, v: 0 }`; the record-level
 * `verse_start` is not a reliable signal (it can be -1). When `bbbId` is
 * provided the search is limited to that video's record.
 */
export const hasIntroSegment = (
  ts: Timestamps[] | undefined,
  chapter: number,
  bbbId?: string
): boolean =>
  ts?.some(
    timestamp =>
      (bbbId === undefined || timestamp.id === bbbId) &&
      timestamp.seg?.some(segment => segment.c === chapter && segment.v === 0)
  ) ?? false;

/**
 * Mid-chapter segment intro URL verse, e.g. "21.0" for the intro of the video
 * that begins at verse 21. Distinct from chapter intro ("0").
 */
export const isSegmentIntroVerse = (verse: string | undefined): boolean =>
  verse !== undefined && /^\d+\.0$/.test(verse);

export const isIntroVerse = (verse: string | undefined): boolean =>
  verse === '0' || isSegmentIntroVerse(verse);

/** "21.0" → "21"; "0" / "21" unchanged. Used to pick the playing video. */
export const selectionVerseFromUrl = (verse: string | undefined): string | undefined => {
  if (verse === undefined) {
    return undefined;
  }

  return isSegmentIntroVerse(verse) ? verse.slice(0, -2) : verse;
};

/** Seek target inside the segment list: segment intros map to verse 0. */
export const seekVerseFromUrl = (verse: string | undefined): string | undefined => {
  if (verse === undefined) {
    return undefined;
  }

  return isIntroVerse(verse) ? '0' : verse;
};

export const segmentIntroVerse = (firstVerse: string): string => `${firstVerse}.0`;

export const getRange = (
  ts: Timestamp[] | undefined,
  chapter: string | undefined,
  verse: string | undefined
) => {
  if (ts !== undefined && chapter !== undefined && verse !== undefined) {
    for (let i = 0; i < ts.length; i += 1) {
      if (chapter === ts[i].c.toString() && verse === ts[i].v.toString()) {
        return {
          start: ts[i].ts,
          end: ts[i + 1]?.ts ?? undefined,
        };
      }
    }
  }

  return undefined;
};

export const getChapterVerse = (
  timestamp: number | undefined | null,
  lut: Timestamp[]
): ChapterVerse | undefined => {
  if (timestamp !== undefined && timestamp !== null) {
    const index = lut.findIndex(i => i.ts > timestamp);
    switch (index) {
      case -1:
        return lut[lut.length - 1];
      case 0:
        return lut[0];
      default:
        return lut[index - 1];
    }
  }

  return undefined;
};

const appendZero = (time: number): string => (time < 10 ? `0${time}` : time.toString());

export const hasHours = (time: number): boolean => time / 3600 >= 1;

export const convertTimestampToString = (time: number, withHours = true): string => {
  const hours = Math.floor(time / 3600);
  const secondsRemaining = time % 3600;
  const minutes = Math.floor(secondsRemaining / 60);
  const seconds = secondsRemaining % 60;

  return `${withHours ? `${appendZero(hours)}:` : ''}${appendZero(minutes)}:${appendZero(seconds)}`;
};

const findPrevTimestamp = (
  currentTimestamp: number,
  timestamps: Timestamp[]
): Timestamp | undefined => timestamps.find(timestamp => currentTimestamp - timestamp.ts > 0.5);

export const seekPrevVerse = (
  currentTime: number,
  lut: Timestamp[] | undefined
): number | undefined => {
  let result;
  lut
    ?.slice()
    .reverse()
    .every(({ ts }, index, self) => {
      if (ts < currentTime) {
        // continue forward to find the next index different from the current ts
        const next = findPrevTimestamp(ts, self.slice(index));
        result = next?.ts;
      }

      return ts >= currentTime;
    });

  return result;
};

export const seekNextVerse = (
  currentTime: number,
  lut: Timestamp[] | undefined
): number | undefined => {
  let result;

  lut?.every(({ ts }) => {
    if (ts > currentTime) {
      result = ts;
    }

    return ts <= currentTime;
  });

  return result;
};

export const isOnAMarker = (currentPosition: number, positions: number[]): boolean => {
  let found = false;
  positions.every(position => {
    const diff = currentPosition - position;

    if (diff >= 0 && diff <= 6) {
      found = true;
    }

    return position <= currentPosition;
  });

  return found;
};

export const getTimestamp = (
  verse: string,
  chapter: string,
  lut: Timestamp[]
): number | undefined => lut.find(i => i.v.toString() === verse && i.c.toString() === chapter)?.ts;

export const convertTimestampToSeconds = (timestamp: string): number => {
  const [m, s, ms] = timestamp.split(':');

  return parseInt(m, 10) * 60 + parseInt(s, 10) + parseInt(ms, 10) / 60;
};
